<template>
  <v-form ref="form" autocomplete="off" @submit.prevent>
    <FormSection>
      <ComplaintFormTabs
        v-model="formData.modelType"
        :items="modelTypeItems"
        :error-messages="modelTypeErrors"
        :grid-four-items="modelTypeItems.length === 4"
        @tab-click="onModelTypeClick"
      />
    </FormSection>

    <FormSection :disabled="disabledFields.formSection" :hidden-disabled-mobile="true">
      <BaseFieldset :disabled="!formData.modelType">
        <BaseAlert
          v-if="visibleFields.impulseReplacementAlert"
          type="warning"
          alignment="md-start"
          :message="$t('complaintForm.ticket.engine.impluse1_replacement_info')"
        />

        <FormSection>
          <BaseSelect
            v-model="orderType"
            class="mb-6"
            :error-messages="orderTypeSelectErrors"
            :hide-details="!orderTypeSelectErrors.length"
            :label="$t('complaintForm.ticket.order_type')"
            :items="orderTypeSelectItems"
            required
            v-on="getTouchListeners('orderType')"
          />

          <BaseAlert
            v-if="visibleFields.selectOrderTypeAlert"
            type="info"
            alignment="md-start"
            :message="$t('complaintForm.ticket.select_order_type')"
          />

          <BaseAlert v-if="blockedBrands.length" type="error" alignment="md-start">
            <template #message>
              <span class="error--text"
                >{{ $t('complaintForm.blockedBrandsNoBuy') }}<br />{{
                  $t('complaintForm.blockedBrands', { list: blockedBrands.join(', ') })
                }}
                <a :href="openItemsUrl">
                  {{ $t('account.invoiceList.invoiceTypes.openitem') }}</a
                ></span
              >
            </template>
          </BaseAlert>

          <BaseFieldset :disabled="disabledFields.calculationDesires">
            <BaseSelect
              v-model="formData.calculationDesire"
              class="mb-6"
              :error-messages="calculationDesireErrors"
              :hide-details="!calculationDesireErrors.length"
              :label="$t('complaintForm.ticket.engine.calculation_wish')"
              :items="calculationWishSelectItems"
              required
              v-on="getTouchListeners('formData.calculationDesire')"
            />

            <BaseAlert
              v-if="visibleFields.selectClaimTypeAlert"
              type="info"
              alignment="md-start"
              :message="$t('complaintForm.ticket.select_claim_type')"
            />

            <FormSection element-type="div">
              <BaseSelect
                v-model="formData.bikeBrand"
                class="mb-6"
                :error-messages="bikeBrandErrors"
                :hide-details="!bikeBrandErrors.length"
                :label="$t('bike_brand')"
                :items="bikeBrandSelectItems"
                required
                :disabled="disabledFields.bikeBrand"
                v-on="getTouchListeners('formData.bikeBrand')"
              />
            </FormSection>

            <FormSection v-if="visibleFields.saleDate" element-type="div">
              <v-row>
                <v-col class="py-0" cols="12" md="6" sm="12">
                  <BaseHeading :level="3" class="mb-1 required">{{
                    $t('complaintForm.ticket.engine.sale_date')
                  }}</BaseHeading>

                  <BaseDatepicker
                    v-model="formData.saleDate"
                    class="mb-6 mb-md-0"
                    :error-messages="saleDateErrors"
                    :hide-details="!saleDateErrors.length"
                    :locale="language"
                    :show-current="false"
                    :max="currentDate"
                    required
                    full-width
                    landscape
                    v-on="getTouchListeners('formData.saleDate')"
                  />
                </v-col>

                <v-col class="py-0" cols="12" md="6" sm="12">
                  <BaseHeading :level="3" class="mb-1 required">{{
                    $t('complaintForm.ticket.engine.purchase_receipt')
                  }}</BaseHeading>

                  <FileDropzone
                    v-model="proofOfPurchase"
                    :error-messages="fileUploadErrors"
                    :hide-details="!fileUploadErrors.length"
                    url="/api/bureturnapi?code=image-upload"
                    required
                    @[onDropzoneSuccess]="setFileUploadPath"
                    @[onDropzoneSending]="setPending(true)"
                    @[onDropzoneComplete]="setPending(false)"
                  />
                </v-col>
              </v-row>
            </FormSection>
          </BaseFieldset>
        </FormSection>
      </BaseFieldset>

      <BaseFieldset :disabled="disabledFields.engineType">
        <FormSection>
          <BaseSelect
            v-if="visibleFields.engineType"
            v-model="motorTypeSelect"
            class="mb-6"
            :error-messages="motorTypeSelectErrors"
            :hide-details="!motorTypeSelectErrors.length"
            :label="$t('complaintForm.ticket.engine.engine_type')"
            :items="motorTypeSelectItems"
            required
            v-on="getTouchListeners('motorTypeSelect')"
          />

          <BaseAlert
            v-if="visibleFields.engineTypeAlert"
            type="info"
            alignment="md-start"
            :message="$t('complaintForm.ticket.select_engine_type')"
            class="mb-12"
          />

          <BaseFieldset class="mt-0" :disabled="disabledFields.motorArticleNr">
            <EngineArticleNumberCB
              v-model="motorArticleNoCB"
              class="mb-6"
              :model-type="formData.modelType"
              :calculation-desire="formData.calculationDesire"
              :with-legacy-device-pick-up="withLegacyDevicePickUp"
              :engine-type="engineType"
              :repair-process-type="formData.repairProcessType"
              :error-messages="motorArticleNoCBErrors"
              :hide-details="!motorArticleNoCBErrors.length"
              :label="$t('complaintForm.ticket.engine.motor_article_no')"
              :add-legacy-engines="isEngineForm"
              :order-type="orderType"
              required
              v-on="getTouchListeners('motorArticleNoCB')"
            />

            <template v-if="visibleFields.engineSeriesNr">
              <BaseTextfield
                v-model="formData.engineSerialNo"
                class="mb-6"
                :error-messages="engineSerialNoErrors"
                :hide-details="!engineSerialNoErrors.length"
                :label="$t('complaintForm.ticket.engine.engine_series_no')"
                counter
                required
                v-on="getTouchListeners('formData.engineSerialNo')"
              />

              <BaseAlert v-if="visibleFields.engineSeriesNrAlert" icon="info">
                <template #message>
                  <BaseRichtext :class="$style.pre">
                    {{ $t('complaintForm.ticket.engine.worktime_tooltip').trim() }}
                  </BaseRichtext>
                </template>
              </BaseAlert>

              <BaseAlert type="warning" icon="info" :message="motorSerialMessage">
                <v-img
                  v-if="visibleFields.modelTypeInfoImage"
                  :src="motorSerialImageUrl"
                  :lazy-src="motorSerialImageUrl"
                  :alt="motorSerialImageAlt"
                  cover
                  position="top center"
                />
              </BaseAlert>
            </template>
          </BaseFieldset>
        </FormSection>

        <FormSection>
          <BaseSelect
            v-if="isContinentalType"
            v-model="formData.bikeType"
            class="mb-6"
            :error-messages="bikeTypeSelectErrors"
            :hide-details="!bikeTypeSelectErrors.length"
            :label="$t('complaintForm.ticket.bike_type')"
            :items="bikeTypeSelectItems"
            required
            v-on="getTouchListeners('formData.bikeType')"
          />
        </FormSection>

        <BaseFieldset v-if="visibleFields.motorDetails" :disabled="disabledFields.motorDetails">
          <FormSection v-if="visibleFields.motorDetailsForm">
            <v-row>
              <v-col cols="12" sm="6" class="py-0">
                <BaseHeading :level="3" class="mb-1 required">{{
                  $t('complaintForm.ticket.engine.again_label')
                }}</BaseHeading>

                <p>{{ $t('complaintForm.ticket.engine.again') }}</p>

                <v-radio-group
                  v-model="formData.again"
                  class="mb-6"
                  :error-messages="againErrors"
                  :hide-details="!againErrors.length"
                  row
                >
                  <v-radio
                    v-for="(item, index) in againItems"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                    color="black"
                    class="mr-9"
                    required
                    v-on="getTouchListeners('formData.again')"
                  />
                </v-radio-group>

                <BaseAlert
                  v-if="visibleFields.motorClaimAlert"
                  type="warning"
                  icon="info"
                  :message="$t('complaintForm.claimNotPossible')"
                />
              </v-col>

              <v-col v-if="visibleFields.softwareClaim" cols="12" sm="6" class="py-0">
                <BaseHeading :level="3" class="mb-1 required">
                  {{ $t('complaintForm.ticket.engine.againlabel_software') }}
                </BaseHeading>

                <p>{{ $t('complaintForm.ticket.engine.again_software') }}</p>

                <v-radio-group
                  v-model="formData.againSoftware"
                  class="mb-6"
                  :error-messages="againSoftwareErrors"
                  :hide-details="!againSoftwareErrors.length"
                  row
                >
                  <v-radio
                    v-for="(item, index) in againSoftwareItems"
                    :key="index"
                    :label="item.label"
                    :value="item.value"
                    color="black"
                    class="mr-9"
                    required
                    v-on="getTouchListeners('formData.againSoftware')"
                  />
                </v-radio-group>
              </v-col>
            </v-row>
          </FormSection>

          <FormSection v-if="visibleFields.bikeDetailsFormAdditional">
            <BaseTextfield
              v-if="visibleFields.mileage"
              v-model="formData.mileage"
              class="mb-6"
              :error-messages="mileageErrors"
              :hide-details="!mileageErrors.length"
              :label="$t('complaintForm.ticket.engine.mileage_in_km')"
              required
              v-on="getTouchListeners('formData.mileage')"
            />

            <!-- TODO: UPDATE (https://docs.google.com/spreadsheets/d/1CbT1j1d7aHwhHRH3x7f4oWtAoQh2n00mlCuKkjFiECI/edit#gid=0) -->
            <BaseTextfield
              v-model="serialNumberBike"
              class="mb-6"
              :error-messages="serialNumberBikeErrors"
              :hide-details="!serialNumberBikeErrors.length"
              :label="$t('complaintForm.ticket.engine.serial_number')"
              required
              v-on="getTouchListeners('serialNumberBike')"
            />

            <BaseAlert type="warning" icon="info" :message="explainBikeSerialTranslation">
              <v-img
                v-if="visibleFields.serialHelpImage"
                :min-height="190"
                :max-height="190"
                :src="bikeSerialImageUrl"
                :lazy-src="bikeSerialImageUrl"
                :alt="imageData.serial.name"
                :class="$style.serialImage"
                cover
              />
            </BaseAlert>
          </FormSection>
        </BaseFieldset>
      </BaseFieldset>

      <BaseFieldset :disabled="disabledFields.repairProcess">
        <FormSection>
          <BaseTextfield
            v-if="visibleFields.repairProcess"
            class="mb-6"
            :hide-details="true"
            :label="$t('complaintForm.ticket.engine.repair_process')"
            :value="
              formData.calculationDesire &&
              formData.repairProcessType &&
              repairProcessTypes[formData.repairProcessType].text
            "
            :readonly="true"
            :disabled="true"
          />

          <div v-if="visibleFields.dealerPickupDate">
            <BaseHeading
              :level="3"
              :class="{ 'mb-1': true, required: !showPickupDateInformation }"
              >{{ $t('complaintForm.ticket.engine.pick-up_date_dealer') }}</BaseHeading
            >

            <BaseAlert
              v-if="visibleFields.dealerPickupDateAlert"
              type="warning"
              alignment="md-start"
              :message="$t('complaintForm.ticket.engine.pick-up_info')"
            />

            <BaseDatepicker
              v-else
              v-model="formData.pickupDate"
              class="mb-6"
              :error-messages="pickupDateErrors"
              :hide-details="!pickupDateErrors.length"
              :locale="language"
              :show-current="false"
              :min="tomorrowDate"
              :allowed-dates="allowedPickupDates"
              required
              full-width
              landscape
              v-on="getTouchListeners('formData.pickupDate')"
            />
          </div>
        </FormSection>
      </BaseFieldset>

      <FormSection v-if="visibleFields.claimNotPossibleAlert">
        <BaseAlert type="warning" icon="info" :message="$t('complaintForm.claimNotPossible')" />
      </FormSection>
    </FormSection>

    <ComplaintFormStepperControls
      :is-valid="isValid"
      :has-required-fields="true"
      :prev-step="prevStep"
      :next-step="submit"
    />
  </v-form>
</template>

<script>
import {
  compareAsc,
  subYears,
  parseISO,
  formatISO,
  startOfToday,
  startOfTomorrow,
  isSunday,
} from 'date-fns'
import { get, replace, compact, head, isObject, isNull } from 'lodash-es'
import { mapMutations, mapGetters } from 'vuex'
import { mask } from 'vue-the-mask'
import {
  required,
  numeric,
  integer,
  minLength,
  maxLength,
  minValue,
  requiredIf,
  // requiredUnless,
} from 'vuelidate/lib/validators'

// import { decimal } from '@/js/components/Forms/helper/form-validation-helper.js'

import StepperMixin from '@/js/mixins/stepper-mixin.js'
import FormValidationMixin from '@/js/components/Forms/mixins/form-validation-mixin.js'
import ComplaintFormMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-default-mixin.js'
import ComplaintMotorDataMixin from './complaint-motor-data-mixin.js'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_MUTATION from '@/js/components/Forms/ComplaintForm/_store-module/mutation-types.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'
import { STORE_MODULES } from '@/js/plugins/store/modules/aimeos/actions/std-imports'
import APP_GETTERS from '@/js/plugins/store/modules/app/getter-types.js'

import FormSection from '@/js/components/Forms/FormSection.vue'
import FileDropzone, { FILE_DROPZONE_EVENTS } from '@/js/components/Shared/FileDropzone.vue'

import EngineArticleNumberCB from '../../components/engine/EngineArticleNumberCB.vue'
import ComplaintFormTabs from '../../ComplaintFormTabs.vue'
import ComplaintFormStepperControls from '../../ComplaintFormStepperControls.vue'

import { SERVICE_BRAND_TO_COMPANY_CODE } from '@/js/helper/dp-usermanagement-constants.js'

export default {
  name: 'ComplaintFormImpulseProductData',
  components: {
    FormSection,
    FileDropzone,
    ComplaintFormTabs,
    ComplaintFormStepperControls,
    EngineArticleNumberCB,
  },
  directives: {
    mask,
  },
  mixins: [FormValidationMixin, ComplaintFormMixin, ComplaintMotorDataMixin],
  props: {
    ...StepperMixin.props,
  },
  data() {
    return {
      // Constants
      ...FORM_CONSTANTS.COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE,
      ...FORM_CONSTANTS.ORDER_TYPES,
      onDropzoneSuccess: FILE_DROPZONE_EVENTS.VDROPZONE_SUCCESS,
      onDropzoneSending: FILE_DROPZONE_EVENTS.VDROPZONE_SENDING,
      onDropzoneComplete: FILE_DROPZONE_EVENTS.VDROPZONE_COMPLETE,
      formData: {
        modelType: this.$route.query?.modelType || null,
        engineSerialNo: null,
        again: null,
        againSoftware: null,
        mileage: null,
        repairProcessType: null,
        calculationDesire: null,
        saleDate: null,
        pickupDate: null,
        fileUpload: '',
        bikeType: '',
        bikeBrand: null,
        ctdiDescriptions: { [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.CALCULATION_DESIRE]: undefined },
      },
      // Other data
      prevModelType: null,
      orderType: null,
      currentDate: formatISO(startOfToday(), { representation: 'date' }),
      tomorrowDate: formatISO(startOfTomorrow(), { representation: 'date' }),
      proofOfPurchase: null,
      proofOfPurchaseError: '',
      serialNumberBike: this.$route.query?.frame || null,
      motorTypeSelect: null,
      motorArticleNoCB: null,
      // validation values
      engineSerialNoMinLength: 5,
      serialNumberBikeMaxLength: 9,
    }
  },
  validations: {
    orderType: {
      required,
    },
    serialNumberBike: {
      required: requiredIf(function () {
        return !this.isBuyEngineForm && this.withLegacyDevicePickUp
      }),
      numeric: {
        value(val) {
          return numeric(replace(val, ' ', ''))
        },
      },
      minLength: minLength(9),
      maxLength: maxLength(10),
    },
    motorTypeSelect: {
      required: requiredIf(function () {
        return !this.isContinentalType
      }),
    },
    motorArticleNoCB: {
      required,
      // required: requiredUnless(function() {
      //   return (
      //     this.isBuyEngineForm ||
      //     this.formData.modelType ===
      //       FORM_CONSTANTS.COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE
      //         .MODEL_TYPE_IMPULSE
      //   )
      // }),
    },
    formData: {
      modelType: {
        required,
      },
      engineSerialNo: {
        required: requiredIf(function () {
          return !this.isBuyEngineForm && this.withLegacyDevicePickUp
        }),
        numeric,
        minLength: minLength(5),
      },
      again: {
        required: requiredIf(function () {
          return !this.isBuyEngineForm && this.withLegacyDevicePickUp
        }),
      },
      againSoftware: {
        required: requiredIf(function () {
          return !this.isContinentalType && !this.isBuyEngineForm && this.withLegacyDevicePickUp
        }),
      },
      bikeType: {
        required: requiredIf(function () {
          return this.isContinentalType
        }),
      },
      mileage: {
        required: requiredIf(function () {
          return !this.isBuyEngineForm && this.withLegacyDevicePickUp
        }),
        integer,
        minValue: minValue(0),
      },
      calculationDesire: {
        required,
      },
      repairProcessType: {
        required,
      },
      pickupDate: {
        required: requiredIf(function () {
          return this.withLegacyDevicePickUp && !this.showPickupDateInformation
        }),
      },
      saleDate: {
        required: requiredIf(function () {
          return this.isWarantyType
        }),
      },
      fileUpload: {
        required: requiredIf(function () {
          return this.isWarantyType
        }),
      },
      bikeBrand: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      language: FORM_GETTER.LANGUAGE,
      ctdiDescriptions: FORM_GETTER.CTDI_DESCRIPTIONS,
      paymentAddressCountryId: FORM_GETTER.PAYMENT_ADDRESS_COUNTRY_ID,
      swBrands: FORM_GETTER.SW_BRANDS,
    }),
    ...mapGetters(STORE_MODULES.APP, {
      companies: APP_GETTERS.USER_COMPANIES,
      getLinkByType: APP_GETTERS.APP_CONFIG_LINK_BY_TYPE,
    }),
    openItemsUrl() {
      return this.getLinkByType('openItems')
    },
    isPickupCountry() {
      const key = 'settings.complaintForm.noPickupCountries'
      const noPickupCountriesSetting = this.$t(key)
      const noPickupCountries =
        key === noPickupCountriesSetting ? [] : noPickupCountriesSetting.split(',')
      return !noPickupCountries.includes(this.paymentAddressCountryId)
    },
    isContinentalType() {
      return (
        this.formData.modelType ===
        FORM_CONSTANTS.COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE
          .MODEL_TYPE_CONTINENTAL
      )
    },
    modelTypeItems() {
      return this.getModelTypeItems({ isBuyEngineForm: this.isBuyEngineForm })
    },
    explainBikeSerialTranslation() {
      return this.$t('complaintForm.ticket.engine.explain_serial')
    },
    explainMotorSerialTranslation() {
      if (this.isContinentalType) {
        return this.$t('complaintForm.ticket.engine.continental_explain_serial')
      } else {
        return this.$t('complaintForm.ticket.engine.explain_serial')
      }
    },
    modelTypeInfo() {
      return this.getModelTypeInfo(this.formData.modelType)
    },
    bikeSerialImageUrl() {
      return this.imageData.serial.imageUrl
    },
    motorSerialImageUrl() {
      return this.isContinentalType
        ? this.imageData.serialContinental.imageUrl
        : this.modelTypeInfo.image.imageUrl
    },
    motorSerialImageAlt() {
      return this.isContinentalType
        ? this.$t('complaintForm.ticket.engine.engine_series_no')
        : this.modelTypeInfo.image.name
    },
    motorSerialMessage() {
      return this.isContinentalType
        ? this.$t('complaintForm.ticket.engine.continental_explain_serial')
        : this.modelTypeInfo.message
    },
    orderTypeSelectItems() {
      return this.getOrderTypeSelectItems({
        modelType: this.formData.modelType,
        isPickupCountry: this.isPickupCountry,
        buyBlock: !!this.blockedBrands.length,
      })
    },
    bikeTypeSelectItems() {
      return this.getBikeTypeSelectItems()
    },
    motorTypeSelectItems() {
      return this.getMotorTypeSelectItems()
    },
    calculationWishSelectItems() {
      return this.getCalculationWishSelectItems({
        isBuyEngineForm: this.isBuyEngineForm,
        orderType: this.orderType,
        isPickupCountry: this.isPickupCountry,
        isContinental: this.isContinentalType,
        buyBlock: !!this.blockedBrands.length,
      })
    },
    /** Computed helper */
    showPickupDateInformation() {
      return [
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_NEW_ENGINE_WITH_LEGACY_DEVICE_PICK_UP,
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_POOL_ENGINE_WITH_LEGACY_DEVICE_PICK_UP,
      ].includes(this.formData.calculationDesire)
    },
    serialNo1() {
      return get(this.serialNumberBike?.split(' '), '0', '')
    },
    serialNo2() {
      return get(this.serialNumberBike?.split(' '), '1', '')
    },
    isFeeBasedType() {
      return (
        this.formData.calculationDesire ===
        FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_FEE_BASED
      )
    },
    isWarantyType() {
      switch (this.formData.calculationDesire) {
        // * Fallthrough
        case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_GUARANTEE:
        case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_WARRANTY_WITHOUT_DEVICE_PICKUP_FOR_NO_PICK_UP_COUNTRIES:
        case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_POOL_ENGINE_IN_WARRANTY_PERIOD:
        case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
          .CALCULATION_DESIRE_NEW_ENGINE_IN_WARRANTY_PERIOD: {
          return true
        }
        default: {
          return false
        }
      }
    },
    canBeClaimed() {
      return this.formData.saleDate
        ? compareAsc(new Date(this.formData.saleDate), subYears(parseISO(this.currentDate), 3)) >= 0
        : true
    },
    motorArticleNo() {
      return isObject(this.motorArticleNoCB) ? this.motorArticleNoCB.value : this.motorArticleNoCB
    },
    motorArticleNoSap() {
      return get(this.motorArticleNoCB, 'valueSap', '')
    },
    withLegacyDevicePickUp() {
      return this.orderType === FORM_CONSTANTS.ORDER_TYPES.WITH_LEGACY_DEVICE_PICK_UP
    },
    inputMotorDetails() {
      return (
        this.orderType === FORM_CONSTANTS.ORDER_TYPES.WITH_LEGACY_DEVICE_PICK_UP ||
        this.isWarantyType
      )
    },

    /** Form data */
    fullFormData() {
      return {
        ...this.formData,
        engineArticleNo: this.motorArticleNo,
        serialNo1: this.serialNo1,
        serialNo2: this.serialNo2,
      }
    },
    disabledFields() {
      return {
        formSection: !this.formData.modelType,
        calculationDesires: !this.orderType,
        engineType: !this.formData.calculationDesire,
        motorArticleNr: !this.isContinentalType && !this.motorTypeSelect,
        motorDetails: !this.isContinentalType && !this.motorTypeSelect,
        repairProcess: !this.isContinentalType && !this.motorTypeSelect,
        bikeBrand: !this.formData.calculationDesire,
      }
    },
    visibleFields() {
      return {
        impulseReplacementAlert:
          this.formData.modelType ===
          FORM_CONSTANTS.COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE
            .MODEL_TYPE_IMPULSE,
        selectOrderTypeAlert: this.formData.modelType && !this.orderType,
        selectClaimTypeAlert: this.orderType && !this.formData.calculationDesire,
        saleDate: this.isWarantyType,
        engineType: !this.isContinentalType,
        engineTypeAlert:
          this.formData.calculationDesire && !this.motorTypeSelect && !this.isContinentalType,
        engineSeriesNr: this.inputMotorDetails || this.isContinentalClaim,
        engineSeriesNrAlert:
          this.formData.modelType ===
          FORM_CONSTANTS.COMPLAINT_FORM_IMPULSE_PRODUCT_DATA_CONSTANTS.MODEL_TYPE
            .MODEL_TYPE_IMPULSE_EVO,
        modelTypeInfoImage: this.modelTypeInfo.image || this.isContinentalClaim,
        againErrorsRadio: !this.isContinentalType || this.isContinentalClaim,
        mileage: !this.isContinentalType || this.isContinentalClaim,
        motorDetails:
          (!this.isBuyEngineForm && (this.inputMotorDetails || !this.orderType)) ||
          this.isContinentalType,
        motorDetailsForm: this.inputMotorDetails || this.isContinentalClaim,
        motorClaimAlert: !this.canBeClaimed,
        softwareClaim: !this.isContinentalType,
        bikeDetailsFormAdditional:
          !this.orderType || this.inputMotorDetails || this.isContinentalType,
        serialHelpImage: this.isContinentalType
          ? this.imageData.serialContinental
          : this.imageData.serial,
        repairProcess: !this.isBuyEngineForm,
        dealerPickupDate: this.withLegacyDevicePickUp,
        dealerPickupDateAlert: this.showPickupDateInformation,
        claimNotPossibleAlert: !this.canBeClaimed,
      }
    },
    isContinentalClaim() {
      return (
        this.isContinentalType &&
        this.orderType !== FORM_CONSTANTS.ORDER_TYPES.WITHOUT_LEGACY_DEVICE_PICK_UP
      )
    },
    engineType() {
      return this.isContinentalType ? 'all' : this.motorTypeSelect
    },
    repairProcessTypeItems() {
      return [
        {
          text: this.$t('complaintForm.ticket.engine.repair_process_types.repair'),
          value: FORM_CONSTANTS.REPAIR_PROCESS_TYPES.REPAIR,
        },
        {
          text: this.$t('complaintForm.ticket.engine.repair_process_types.pool'),
          value: FORM_CONSTANTS.REPAIR_PROCESS_TYPES.POOL,
        },
      ]
    },
    repairProcessTypes() {
      return {
        1: {
          text: this.$t('complaintForm.ticket.engine.repair_process_types.pool'),
          value: FORM_CONSTANTS.REPAIR_PROCESS_TYPES.REPAIR,
        },
        2: {
          text: this.$t('complaintForm.ticket.engine.repair_process_types.repair'),
          value: FORM_CONSTANTS.REPAIR_PROCESS_TYPES.REPAIR,
        },
        3: {
          text: this.$t('complaintForm.ticket.engine.repair_process_types.new_engine'),
          value: FORM_CONSTANTS.REPAIR_PROCESS_TYPES.REPAIR,
        },
        4: {
          text: this.$t('complaintForm.ticket.engine.repair_process_types.warrantyWithoutPickup'),
          value: FORM_CONSTANTS.REPAIR_PROCESS_TYPES.REPAIR_WITHOUT_PICKUP,
        },
      }
    },
    // calculationWishSelectItems() {
    //   if (this.isBuyEngineForm) {
    //     return this.getCalculationWishSelectItemsBuy()
    //   }
    //   return this.getCalculationWishSelectItems()
    // },
    /** Radio items */
    againItems() {
      return [
        {
          label: this.$t('complaintForm.ticket.engine.again1'),
          value: this.$t('complaintForm.ticket.engine.again1'),
        },
        {
          label: this.$t('complaintForm.ticket.engine.again2'),
          value: this.$t('complaintForm.ticket.engine.again2'),
        },
      ]
    },
    againSoftwareItems() {
      return [
        {
          label: this.$t('complaintForm.ticket.engine.again1_software'),
          value: this.$t('complaintForm.ticket.engine.again1_software'),
        },
        {
          label: this.$t('complaintForm.ticket.engine.again2_software'),
          value: this.$t('complaintForm.ticket.engine.again2_software'),
        },
      ]
    },

    /** Errors */
    orderTypeSelectErrors() {
      return this.formErrorHelper('orderType', [this.chooseError])
    },
    bikeTypeSelectErrors() {
      return this.formErrorHelper('formData.bikeType', [this.chooseError])
    },
    modelTypeErrors() {
      return this.formErrorHelper('formData.modelType', [this.chooseError])
    },
    motorTypeSelectErrors() {
      return this.formErrorHelper('motorTypeSelect', [this.chooseError])
    },
    motorArticleNoCBErrors() {
      return this.formErrorHelper('motorArticleNoCB', [this.requiredError])
    },
    engineSerialNoErrors() {
      return this.formErrorHelper('formData.engineSerialNo', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.engineSerialNoMinLength),
      ])
    },
    againErrors() {
      return this.formErrorHelper('formData.again', [this.chooseError])
    },
    againSoftwareErrors() {
      return this.formErrorHelper('formData.againSoftware', [this.chooseError])
    },
    mileageErrors() {
      return this.formErrorHelper('formData.mileage', [
        this.requiredError,
        {
          condition: 'integer',
          error: this.numbersErrorMessage,
        },
      ])
    },
    serialNumberBikeErrors() {
      return this.formErrorHelper('serialNumberBike', [
        this.requiredError,
        this.numericError,
        this.minLengthError(this.serialNumberBikeMaxLength),
        this.maxLengthError(this.serialNumberBikeMaxLength),
      ])
    },
    calculationDesireErrors() {
      return this.formErrorHelper('formData.calculationDesire', [this.chooseError])
    },
    repairProcessTypeErrors() {
      return this.formErrorHelper('formData.repairProcessType', [this.chooseError])
    },
    saleDateErrors() {
      return this.formErrorHelper('formData.saleDate', [this.requiredError])
    },
    pickupDateErrors() {
      return this.formErrorHelper('formData.pickupDate', [this.requiredError])
    },
    fileUploadErrors() {
      return compact([
        this.proofOfPurchaseError,
        ...this.formErrorHelper('formData.fileUpload', [
          {
            condition: 'required',
            error: this.fileErrorMessage,
          },
        ]),
      ])
    },
    bikeBrandErrors() {
      return this.formErrorHelper('formData.bikeBrand', [this.requiredError])
    },
    userCompanies() {
      return this.companies.map(company => company.id)
    },
    fallbackCompany() {
      if (this.userCompanies.includes('1010')) {
        // Mixed users, make sure to always take kalkhoff
        return '1010'
      } else {
        // No mixed user, so just take the one available
        return this.userCompanies[0]
      }
    },
    bikeBrandSelectItems() {
      return this.swBrands
        .filter(b => !['UA'].includes(b.brand))
        .map(b => {
          return {
            text: this.$t(`brand.${b.brand}`),
            value: `${b.brand}|${b.companyCode}`,
            // disabled: !!b.blocked,
          }
        })
    },
    blockedBrands() {
      return this.swBrands.filter(b => b.blocked).map(b => this.$t(`brand.${b.brand}`))
    },
    // bikeBrandSelectItems() {
    //   return Object.entries(SERVICE_BRAND_TO_COMPANY_CODE)
    //     .map(([brand, companyCodes]) => {
    //       const companyCode =
    //         companyCodes.find(code => this.userCompanies.includes(code)) ?? this.fallbackCompany
    //       return {
    //         text: this.$t(`brand.${brand}`),
    //         value: `${brand}|${companyCode}`,
    //       }
    //     })
    //     .filter(item => !!item.value)
    // },
  },
  watch: {
    'formData.modelType'(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }

      this.orderType = null
      console.log(
        'motorTypeSelect',
        this.$route.query?.motorTypeKey &&
          (isNull(this.motorTypeSelect) ||
            this.motorTypeSelect === this.$route.query?.motorTypeKey) &&
          !!this.motorTypeSelectItems.find(item => item.value === this.$route.query?.motorTypeKey)
          ? this.$route.query?.motorTypeKey
          : null,
      )

      this.motorTypeSelect =
        this.$route.query?.motorTypeKey &&
        (isNull(this.motorTypeSelect) ||
          this.motorTypeSelect === this.$route.query?.motorTypeKey) &&
        !!this.motorTypeSelectItems.find(item => item.value === this.$route.query?.motorTypeKey)
          ? this.$route.query?.motorTypeKey
          : null
      this.formData.engineSerialNo = null
    },
    orderTypeSelectItems() {
      // Select option if it is only one
      this.orderType =
        this.orderTypeSelectItems.length === 1
          ? get(head(this.orderTypeSelectItems), 'value', this.orderType)
          : this.orderType
    },
    // calculationWishSelectItems() {
    //   console.log(
    //     'calculationWishSelectItems',
    //     this.calculationWishSelectItems,
    //     this.formData.calculationDesire,
    //   )
    //   this.formData.calculationDesire =
    //     this.calculationWishSelectItems.length === 1
    //       ? get(this.calculationWishSelectItems, [0, 'value'], this.formData.calculationDesire)
    //       : this.formData.calculationDesire
    //   console.log('calculationWishSelectItems Res', this.formData.calculationDesire)
    // },
    orderType(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }

      this.formData.repairProcessType = null
      this.formData.calculationDesire = null
      console.log(
        'motorTypeSelect',
        this.$route.query?.motorTypeKey &&
          (isNull(this.motorTypeSelect) ||
            this.motorTypeSelect === this.$route.query?.motorTypeKey) &&
          !!this.motorTypeSelectItems.find(item => item.value === this.$route.query?.motorTypeKey)
          ? this.$route.query?.motorTypeKey
          : null,
      )

      this.motorTypeSelect =
        this.$route.query?.motorTypeKey &&
        (isNull(this.motorTypeSelect) ||
          this.motorTypeSelect === this.$route.query?.motorTypeKey) &&
        !!this.motorTypeSelectItems.find(item => item.value === this.$route.query?.motorTypeKey)
          ? this.$route.query?.motorTypeKey
          : null

      const errorDescStepNumber = 3
      if (!this.isBuyEngineForm && !this.withLegacyDevicePickUp) {
        this.addSkippedStep(errorDescStepNumber)
      } else {
        this.removeSkippedStep(errorDescStepNumber)
      }

      // prevent jump to bottom when changing orderType (DCWSUPPORTDS-1600)
      this.$nextTick(() => {
        window.scrollTo(0, 0)
      })
    },
    modelTypeItems: {
      handler: function (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.formData.modelType = get(this.modelTypeItems, '0.value', null)
        }
      },
    },
    motorTypeSelect(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }

      this.motorArticleNoCB = null
    },
    'formData.calculationDesire'(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }
      console.log(
        'motorTypeSelect',
        this.$route.query?.motorTypeKey &&
          (isNull(this.motorTypeSelect) ||
            this.motorTypeSelect === this.$route.query?.motorTypeKey) &&
          !!this.motorTypeSelectItems.find(item => item.value === this.$route.query?.motorTypeKey)
          ? this.$route.query?.motorTypeKey
          : null,
      )

      this.motorTypeSelect =
        this.$route.query?.motorTypeKey &&
        (isNull(this.motorTypeSelect) ||
          this.motorTypeSelect === this.$route.query?.motorTypeKey) &&
        !!this.motorTypeSelectItems.find(item => item.value === this.$route.query?.motorTypeKey)
          ? this.$route.query?.motorTypeKey
          : null

      // Reset child values
      this.proofOfPurchase = null
      this.formData.saleDate = null
      this.formData.fileUpload = null

      // Set repair process value
      this.setRepairProcessType()
      this.setCtdiDescriptions()
    },
    'formData.repairProcessType'(newValue, oldValue) {
      if (oldValue === newValue) {
        return
      }

      this.formData.pickupDate = null
    },
    proofOfPurchase(newFiles, oldFiles) {
      if (oldFiles === newFiles) {
        return
      }

      if (this.proofOfPurchase?.length === 0) {
        this.proofOfPurchaseError = ''
        this.formData.fileUpload = ''
      }
    },
    motorArticleNoSap() {
      this.setCtdiDescriptions()
    },
    // canBeClaimed(newValue, oldValue) {
    //   if (!this.formData.again || oldValue === newValue) {
    //     return
    //   }

    //   if (newValue === false) {
    //     this.formData.again = this.$t('complaintForm.ticket.engine.again2')
    //   } else {
    //     this.formData.again = null
    //   }
    // },
  },
  methods: {
    ...mapMutations(FORM_CONSTANTS.STORE_ID, {
      updateFormData: FORM_MUTATION.UPDATE_FORM_DATA,
      setPending: FORM_MUTATION.SET_FORM_PENDING,
      addSkippedStep: FORM_MUTATION.ADD_SKIPPED_STEP,
      removeSkippedStep: FORM_MUTATION.REMOVE_SKIPPED_STEP,
    }),
    submitCallback() {
      this.updateFormData(this.fullFormData)
      this.nextStep()
    },
    setFileUploadPath({ response }) {
      const fileUploadPath = get(response, 'files.0', '')
      this.formData.fileUpload = get(response, 'files.0', '')

      if (!fileUploadPath) {
        const fileUploadError = get(response, 'error')

        if (fileUploadError) {
          this.proofOfPurchaseError = this.$t('form.dropzone.uploadFailed')
        }
      }
    },
    setRepairProcessType() {
      this.formData.repairProcessType = get(
        FORM_CONSTANTS.CALCULATION_DESIRE_TO_REPAIR_PROCESS_TYPE,
        this.formData.calculationDesire,
      )
      // switch (this.formData.calculationDesire) {
      //   // RepairProcessType: 1   (* Fallthrough)
      //   case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
      //     .CALCULATION_DESIRE_POOL_ENGINE_IN_WARRANTY_PERIOD:
      //   case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
      //     .CALCULATION_DESIRE_POOL_ENGINE_WITHOUT_LEGACY_DEVICE_PICK_UP:
      //   case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES
      //     .CALCULATION_DESIRE_POOL_ENGINE_WITH_LEGACY_DEVICE_PICK_UP: {
      //     this.formData.repairProcessType = FORM_CONSTANTS.REPAIR_PROCESS_TYPES.POOL
      //     break
      //   }

      //   // RepairProcessType: 2   (* Fallthrough)
      //   case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_FEE_BASED:
      //   case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_GUARANTEE: {
      //     this.formData.repairProcessType = FORM_CONSTANTS.REPAIR_PROCESS_TYPES.REPAIR
      //     break
      //   }

      //   // RepairProcessType: 3
      //   case FORM_CONSTANTS.CALCULATION_DESIRE_TYPES.CALCULATION_DESIRE_NEW_ENGINE: {
      //     this.formData.repairProcessType = FORM_CONSTANTS.REPAIR_PROCESS_TYPES.NEW_ENGINE
      //     break
      //   }

      //   default: {
      //     this.formData.repairProcessType = null
      //   }
      // }
    },
    allowedPickupDates: val => !isSunday(new Date(val)),
    setCtdiDescriptions() {
      const calculationDesireItem = this.calculationWishSelectItems.find(
        i => i.value === this.formData.calculationDesire,
      )

      this.formData.ctdiDescriptions = {
        ...this.ctdiDescriptions,
        [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.CALCULATION_DESIRE]: calculationDesireItem
          ? get(calculationDesireItem, 'text', this.formData.calculationDesire)
          : this.formData.calculationDesire,
        [FORM_CONSTANTS.CTDI_DESCRIPTIONS_KEYS.ENGINE_ARTICLE_NO_SAP]: this.motorArticleNoSap,
      }
    },
    onModelTypeClick(val) {
      if (val === this.prevModelType) {
        this.formData.modelType = null
      }

      this.prevModelType = this.formData.modelType
    },
  },
}
</script>

<style lang="scss" module>
.pre {
  white-space: pre-line;
}

.serialImage {
  @media screen and (min-width: map-get($grid-breakpoints, 'md')) {
    :global(.v-image__image) {
      background-position: top center !important;
    }
  }

  :global(.v-image__image) {
    background-position: top left !important;
  }
}
</style>
